import {
	autoUpdate,
	FloatingPortal,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useRole,
	useTransitionStyles,
} from '@floating-ui/react'
import { Fragment, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import LogoutModal from './Logout'
const StyledProfile = styled.div`
	align-items: center;
	background-color: #e4e9ed;
	border-radius: 23px;
	border: 2px solid #c2c9d0;
	color: #565656;
	cursor: pointer;
	display: flex;
	font-size: 20px;
	font-weight: 500;
	height: 46px;
	justify-content: center;
	user-select: none;
	width: 46px;
	.dark & {
		background-color: #4b4b4b;
		border: 2px solid #ffffff33;
		color: #ffffff;
	}
`
const StyledProfile2 = styled.div`
	border-radius: 10px;
	display: flex;
	height: 60px;
	padding: 8px;
	position: relative;
	user-select: none;
	width: 100%;
	& .icon {
		align-items: center;
		background-color: #e4e9ed;
		border-radius: 50%;
		border: 2px solid #c2c9d0;
		color: #565656;
		display: flex;
		font-size: 20px;
		font-weight: 500;
		height: 40px;
		justify-content: center;
		width: 40px;
		.dark & {
			border: 2px solid #ffffff33;
			background-color: #4b4b4b;
			color: #ffffff;
		}
	}
	& .info {
		padding-left: 8px;
		width: calc(100% - 40px);
		& .name {
			font-size: 14px;
			font-weight: 500;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			width: 100%;
			.dark & {
				color: #ffffff;
			}
		}
		& .position {
			color: #565656;
			font-size: 13px;
			font-weight: 400;
			margin: 4px 0 0 0;
			.dark & {
				color: #acacac;
			}
		}
	}
`
const Profile = ({ collapsed = false }) => {
	const navigate = useNavigate()
	const [visible, setVisible] = useState(false)
	const FirstName = useSelector(state => state?.user?.user?.first_name)
	const LastName = useSelector(state => state?.user?.user?.last_name)
	const Role = useSelector(state => state?.user?.user?.user_type)

	const nameFirstLetter = useMemo(() => {
		const nameFirstLetter = String(FirstName || '')
			.replace(/"/, '')
			.slice(0, 1)
		return nameFirstLetter
	}, [FirstName])
	const [isOpen, setIsOpen] = useState(false)
	const { refs, floatingStyles, context } = useFloating({
		onOpenChange: setIsOpen,
		open: isOpen,
		placement: 'bottom-end',
		strategy: 'fixed',
		whileElementsMounted: autoUpdate,
	})
	const { styles, isMounted } = useTransitionStyles(context, {
		common: { transformOrigin: 'top right' },
		duration: 300,
		initial: {
			opacity: 0,
			transform: 'scale(0.7)',
		},
		open: {
			opacity: 1,
			transform: 'scale(1)',
		},
	})
	const click = useClick(context)
	const dismiss = useDismiss(context)
	const role = useRole(context)
	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		dismiss,
		role,
	])
	return (
		<Fragment>
			<StyledProfile
				{...getReferenceProps()}
				ref={refs.setReference}
				data-collapsed={collapsed}
			>
				<div className='content'>{nameFirstLetter}</div>
			</StyledProfile>
			{isMounted && (
				<FloatingPortal id='floating-ui-portal'>
					<div
						{...getFloatingProps()}
						ref={refs.setFloating}
						style={floatingStyles}
					>
						<div
							className='border-[1px] border-[#e5e7eb] dark:border-[#545454] border-solid rounded-[16px] m-0 overflow-hidden w-[250px] bg-[#ffffff] dark:bg-[#2E2D2D]'
							style={styles}
						>
							<div className='p-2 flex flex-col'>
								<StyledProfile2>
									<div className='icon'>{nameFirstLetter}</div>
									<div className='info'>
										<div className='name'>
											{FirstName + ' ' + LastName || '-'}
										</div>
										<div className='position'>{Role}</div>
									</div>
								</StyledProfile2>
								<button
									className='mt-[16px] group duration-[300ms] border-[1px] border-[#e5e7eb] dark:border-[#FFFFFF59] border-solid items-center justify-center text-[15px] font-medium rounded-[10px] flex h-full outline-none px-[12px] py-[8px] w-full hover:bg-[#d32f2f] hover:text-[#ffffff] hover:border-[#d32f2f] dark:hover:border-[#d32f2f] text-[#292b32] dark:text-[#e1e1e1]'
									onClick={() => {
										setVisible(true)
										setIsOpen(false)
									}}
								>
									<svg fill='none' height='18' viewBox='0 0 16 16' width='18'>
										<path
											clipRule='evenodd'
											d='M12.0221 5.78562C12.185 5.64989 12.3952 5.58443 12.6065 5.60363C12.8177 5.62283 13.0126 5.72512 13.1485 5.88802L14.8149 7.88802C14.9447 8.05162 15.0054 8.25951 14.9839 8.46725C14.9625 8.675 14.8608 8.86614 14.7003 8.99985C14.5399 9.13355 14.3335 9.19921 14.1253 9.1828C13.9171 9.16639 13.7236 9.06921 13.5861 8.91202L11.9197 6.91202C11.7839 6.74905 11.7185 6.53884 11.7377 6.32762C11.7569 6.1164 11.8592 5.92145 12.0221 5.78562Z'
											fill='currentColor'
											fillRule='evenodd'
										/>
										<path
											clipRule='evenodd'
											d='M12.0225 11.0144C11.8595 10.8786 11.7571 10.6837 11.7377 10.4725C11.7184 10.2613 11.7837 10.051 11.9193 9.88795L13.5865 7.88795C13.6528 7.80439 13.7352 7.73493 13.8287 7.68366C13.9223 7.63239 14.0251 7.60035 14.1312 7.58942C14.2373 7.57849 14.3446 7.58888 14.4466 7.62C14.5486 7.65113 14.6434 7.70234 14.7254 7.77063C14.8073 7.83892 14.8748 7.92291 14.9238 8.01766C14.9728 8.11241 15.0024 8.216 15.0107 8.32234C15.0191 8.42869 15.0062 8.53563 14.9726 8.63689C14.939 8.73815 14.8856 8.83167 14.8153 8.91196L13.1489 10.912C13.0131 11.0749 12.8181 11.1771 12.6069 11.1963C12.3957 11.2155 12.1855 11.1501 12.0225 11.0144Z'
											fill='currentColor'
											fillRule='evenodd'
										/>
										<path
											clipRule='evenodd'
											d='M14.0004 8.4C14.0004 8.61217 13.9161 8.81566 13.7661 8.96569C13.616 9.11571 13.4126 9.2 13.2004 9.2H8.00039C7.78822 9.2 7.58473 9.11571 7.43471 8.96569C7.28468 8.81566 7.20039 8.61217 7.20039 8.4C7.20039 8.18783 7.28468 7.98434 7.43471 7.83431C7.58473 7.68429 7.78822 7.6 8.00039 7.6H13.2004C13.4126 7.6 13.616 7.68429 13.7661 7.83431C13.9161 7.98434 14.0004 8.18783 14.0004 8.4ZM2.40039 2.8C2.40039 2.58783 2.48468 2.38434 2.63471 2.23431C2.78473 2.08429 2.98822 2 3.20039 2H10.4004C10.6126 2 10.816 2.08429 10.9661 2.23431C11.1161 2.38434 11.2004 2.58783 11.2004 2.8C11.2004 3.01217 11.1161 3.21566 10.9661 3.36569C10.816 3.51571 10.6126 3.6 10.4004 3.6H3.20039C2.98822 3.6 2.78473 3.51571 2.63471 3.36569C2.48468 3.21566 2.40039 3.01217 2.40039 2.8ZM2.40039 14C2.40039 13.7878 2.48468 13.5843 2.63471 13.4343C2.78473 13.2843 2.98822 13.2 3.20039 13.2H10.4004C10.6126 13.2 10.816 13.2843 10.9661 13.4343C11.1161 13.5843 11.2004 13.7878 11.2004 14C11.2004 14.2122 11.1161 14.4157 10.9661 14.5657C10.816 14.7157 10.6126 14.8 10.4004 14.8H3.20039C2.98822 14.8 2.78473 14.7157 2.63471 14.5657C2.48468 14.4157 2.40039 14.2122 2.40039 14Z'
											fill='currentColor'
											fillRule='evenodd'
										/>
										<path
											clipRule='evenodd'
											d='M10.4004 2C10.6126 2 10.816 2.08429 10.9661 2.23431C11.1161 2.38434 11.2004 2.58783 11.2004 2.8V6C11.2004 6.21217 11.1161 6.41566 10.9661 6.56569C10.816 6.71571 10.6126 6.8 10.4004 6.8C10.1882 6.8 9.98473 6.71571 9.83471 6.56569C9.68468 6.41566 9.60039 6.21217 9.60039 6V2.8C9.60039 2.58783 9.68468 2.38434 9.83471 2.23431C9.98473 2.08429 10.1882 2 10.4004 2ZM10.4004 10C10.6126 10 10.816 10.0843 10.9661 10.2343C11.1161 10.3843 11.2004 10.5878 11.2004 10.8V14C11.2004 14.2122 11.1161 14.4157 10.9661 14.5657C10.816 14.7157 10.6126 14.8 10.4004 14.8C10.1882 14.8 9.98473 14.7157 9.83471 14.5657C9.68468 14.4157 9.60039 14.2122 9.60039 14V10.8C9.60039 10.5878 9.68468 10.3843 9.83471 10.2343C9.98473 10.0843 10.1882 10 10.4004 10ZM3.20039 2C3.41256 2 3.61605 2.08429 3.76608 2.23431C3.91611 2.38434 4.00039 2.58783 4.00039 2.8V14C4.00039 14.2122 3.91611 14.4157 3.76608 14.5657C3.61605 14.7157 3.41256 14.8 3.20039 14.8C2.98822 14.8 2.78473 14.7157 2.63471 14.5657C2.48468 14.4157 2.40039 14.2122 2.40039 14V2.8C2.40039 2.58783 2.48468 2.38434 2.63471 2.23431C2.78473 2.08429 2.98822 2 3.20039 2Z'
											fill='currentColor'
											fillRule='evenodd'
										/>
									</svg>
									<span className='ml-[4px] leading-none'>
										Profildan chiqish
									</span>
								</button>
							</div>
						</div>
					</div>
				</FloatingPortal>
			)}
			<LogoutModal visible={visible} setVisible={setVisible} />
		</Fragment>
	)
}
export default Profile
